import { useEffect, useState, createContext, useContext, useCallback } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { authMe, createSession, logout as apiLogout, clearToken } from "./lib/api";

const AuthCtx = createContext({ user: null, loading: true, refresh: () => {}, logout: () => {} });
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try { const u = await authMe(); setUser(u); return u; }
    catch { setUser(null); return null; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    refresh();
  }, [refresh]);

  const logout = async () => { try { await apiLogout(); } finally { clearToken(); setUser(null); } };

  return <AuthCtx.Provider value={{ user, loading, refresh, setUser, logout }}>{children}</AuthCtx.Provider>;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const m = location.hash.match(/session_id=([^&]+)/);
      if (!m) { nav("/admin/giris"); return; }
      try {
        await createSession(m[1]);
        window.history.replaceState(null, "", "/admin");
        const u = await refresh();
        if (!cancelled) {
          if (u?.is_admin) nav("/admin");
          else nav("/admin/giris?error=not_admin");
        }
      } catch {
        setError("Oturum oluşturulamadı");
        setTimeout(() => nav("/admin/giris"), 1500);
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-[#C0562F] rounded-full animate-spin mx-auto mb-4" />
        <div className="font-display text-lg text-slate-900">{error || "Oturum açılıyor…"}</div>
      </div>
    </div>
  );
}

export function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-[#C0562F] rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/giris" replace />;
  if (!user.is_admin) return <Navigate to="/admin/giris?error=not_admin" replace />;
  return children;
}
